/**
 * 정산 변환기 — 레거시 `settlementInfoGenerator.js` 포팅.
 * 브라우저에서 xls/xlsx를 읽어 표준 정산 컬럼으로 변환·미리보기·멀티시트 내보내기.
 */
import { MenuWriteGate } from '@/shared/components/MenuWriteGate';
import { FileUploadButton } from '@/shared/components/FileUploadButton';
import { PageHeader } from '@/shared/components/PageHeader';
import { SimplePagination } from '@/shared/components/SimplePagination';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/shared/components/ui/alert';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { ToolbarRow } from '@/shared/components/ui/row';
import { useSettlementInfoGenerator } from '@/pages/settlements/useSettlementInfoGenerator';
import { FileSpreadsheet, Trash2 } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';

const STATUS_VARIANT: Record<
  string,
  'success' | 'warning' | 'info' | 'secondary' | 'destructive'
> = {
  parsed: 'success',
  empty: 'warning',
  unmatched: 'info',
  unsupported: 'secondary',
  error: 'destructive',
};

const STATUS_LABEL: Record<string, string> = {
  parsed: '추출 완료',
  empty: '상세 행 없음',
  unmatched: '템플릿 미매칭',
  unsupported: '미지원',
  error: '오류',
};

const PREVIEW_PAGE_SIZE_OPTIONS = [50, 100, 200] as const;

const formatNumber = (value: number | null | undefined | string): string => {
  if (value == null || value === '') return '-';
  return Number(value).toLocaleString('ko-KR');
};

const formatRate = (value: number | null | undefined | string): string => {
  if (value == null || value === '') return '-';
  return `${value}%`;
};

export const SettlementInfoGeneratorPage: React.FC = () => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewPage, setPreviewPage] = useState(1);
  const [previewPageSize, setPreviewPageSize] = useState(50);
  const {
    isLoading,
    summary,
    rows,
    files,
    selectedFiles,
    errorMessage,
    addFilesToProcess,
    removeFileToProcess,
    generatePreview,
    exportPreview,
    clearPreview,
  } = useSettlementInfoGenerator();

  useEffect(() => {
    setPreviewPage(1);
  }, [rows.length]);

  const previewRows = rows.slice(
    (previewPage - 1) * previewPageSize,
    previewPage * previewPageSize,
  );

  return (
    <div className="space-y-5">
      <PageHeader
        icon={FileSpreadsheet}
        title="정산 변환기"
        description="브라우저에서 xls/xlsx 파일을 직접 읽어 정산 컬럼으로 표준화합니다. 여러 파일을 한 번에 선택해도 서버 API 없이 현재 페이지 안에서만 계산합니다. PDF는 현재 MVP 범위에서 미지원입니다."
      />

      <ToolbarRow gap={8}>
        <MenuWriteGate>
          <FileUploadButton
            inputRef={inputRef}
            className="ruda-button-outline"
            accept=".xlsx,.xls,.pdf"
            multiple
            onFilesSelected={(files) =>
              addFilesToProcess(Array.from(files ?? []))
            }
          >
            파일 선택
          </FileUploadButton>
          <Button
            type="button"
            className="ruda-button"
            onClick={() => generatePreview()}
            disabled={isLoading || selectedFiles.length === 0}
          >
            <FileSpreadsheet className="mr-2 h-4 w-4" />
            {isLoading ? '생성 중...' : '정산 정보 생성'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={clearPreview}
            disabled={selectedFiles.length === 0 && rows.length === 0}
          >
            초기화
          </Button>
        </MenuWriteGate>
        <Button
          type="button"
          variant="outline"
          onClick={exportPreview}
          disabled={rows.length === 0}
        >
          엑셀 내보내기
        </Button>
      </ToolbarRow>

      <Alert>
        <AlertTitle>
          선택 파일 {formatNumber(selectedFiles.length)}개
        </AlertTitle>
        <AlertDescription>
          여러 xlsx/xls/pdf 파일을 한 번에 선택해서 가져올 수 있습니다.
        </AlertDescription>
      </Alert>

      {errorMessage ? (
        <Alert variant="destructive">
          <AlertTitle>{errorMessage}</AlertTitle>
        </Alert>
      ) : null}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">선택 파일 목록</CardTitle>
          <span className="text-sm text-muted-foreground">
            총 {formatNumber(selectedFiles.length)}개
          </span>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>파일명</TableHead>
                  <TableHead className="w-28 text-right">크기</TableHead>
                  <TableHead className="w-24 text-center">삭제</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {selectedFiles.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      className="text-center text-muted-foreground"
                    >
                      선택된 파일이 없습니다.
                    </TableCell>
                  </TableRow>
                ) : (
                  selectedFiles.map((file) => (
                    <TableRow key={file.id}>
                      <TableCell>{file.name}</TableCell>
                      <TableCell className="text-right">
                        {formatNumber(Math.ceil(file.size / 1024))} KB
                      </TableCell>
                      <TableCell className="text-center">
                        <MenuWriteGate>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="text-destructive"
                            onClick={() => removeFileToProcess(file.id)}
                          >
                            <Trash2 className="mr-1 h-4 w-4" />
                            삭제
                          </Button>
                        </MenuWriteGate>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">전체 파일</p>
            <p className="text-2xl font-semibold">
              {formatNumber(summary.totalFiles)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">추출 완료 파일</p>
            <p className="text-2xl font-semibold">
              {formatNumber(summary.parsedFiles)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">추출 행 수</p>
            <p className="text-2xl font-semibold">
              {formatNumber(summary.extractedRows)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">거래처/병의원 수</p>
            <p className="text-2xl font-semibold">
              {formatNumber(summary.counterparties)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">템플릿 미매칭</p>
            <p className="text-2xl font-semibold">
              {formatNumber(summary.unmatchedFiles)}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">파일별 처리 현황</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>파일명</TableHead>
                  <TableHead>제약사명</TableHead>
                  <TableHead>적용 템플릿</TableHead>
                  <TableHead>처리 상태</TableHead>
                  <TableHead>추출 건수</TableHead>
                  <TableHead>메시지</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {files.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-center text-muted-foreground"
                    >
                      아직 처리된 파일이 없습니다.
                    </TableCell>
                  </TableRow>
                ) : (
                  files.map((f) => (
                    <TableRow key={f.fileName}>
                      <TableCell>{f.fileName}</TableCell>
                      <TableCell>{f.pharmaceuticalCompanyName || '-'}</TableCell>
                      <TableCell>{f.templateName || '-'}</TableCell>
                      <TableCell>
                        <Badge variant={STATUS_VARIANT[f.status] ?? 'secondary'}>
                          {STATUS_LABEL[f.status] ?? f.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {formatNumber(f.extractedRowCount)}
                      </TableCell>
                      <TableCell>{f.message || '-'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">정산 정보 미리보기</CardTitle>
          <span className="text-sm text-muted-foreground">
            총 {formatNumber(rows.length)}건
          </span>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="max-h-[640px] overflow-auto rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="sticky left-0 z-10 bg-background">
                    제약사명
                  </TableHead>
                  <TableHead className="sticky left-[120px] z-10 bg-background min-w-[200px]">
                    병의원명
                  </TableHead>
                  <TableHead>의약품 코드</TableHead>
                  <TableHead className="min-w-[200px]">의약품명</TableHead>
                  <TableHead className="text-right">단가</TableHead>
                  <TableHead className="text-right">처방수량</TableHead>
                  <TableHead className="text-right">처방금액</TableHead>
                  <TableHead>정산수수료율</TableHead>
                  <TableHead className="text-right">정산금액</TableHead>
                  <TableHead className="min-w-[160px]">비고</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {previewRows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={10}
                      className="text-center text-muted-foreground"
                    >
                      미리보기 데이터가 없습니다.
                    </TableCell>
                  </TableRow>
                ) : (
                  previewRows.map((row, idx) => (
                    <TableRow
                      key={
                        row.id ??
                        `${row.sourceFileName ?? row.sourceFile}-${(previewPage - 1) * previewPageSize + idx}`
                      }
                    >
                      <TableCell className="sticky left-0 bg-background">
                        {row.pharmaceuticalCompanyName || '-'}
                      </TableCell>
                      <TableCell className="sticky left-[120px] bg-background">
                        {row.customerName || '-'}
                      </TableCell>
                      <TableCell>{row.drugCode || '-'}</TableCell>
                      <TableCell>{row.drugName || '-'}</TableCell>
                      <TableCell className="text-right">
                        {formatNumber(row.unitPrice)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatNumber(row.quantity)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatNumber(row.prescriptionAmount)}
                      </TableCell>
                      <TableCell>{formatRate(row.feeRatePercent)}</TableCell>
                      <TableCell className="text-right">
                        {formatNumber(row.settlementAmount)}
                      </TableCell>
                      <TableCell>{row.note || '-'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <SimplePagination
            page={previewPage}
            pageSize={previewPageSize}
            total={rows.length}
            onPageChange={setPreviewPage}
            onPageSizeChange={(size) => {
              setPreviewPageSize(size);
              setPreviewPage(1);
            }}
            pageSizeOptions={PREVIEW_PAGE_SIZE_OPTIONS}
            size="sm"
          />
        </CardContent>
      </Card>
    </div>
  );
};
